import { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { BackNavLink } from '../../components/NavButtons';
import { getStudentClassById } from '../../services/classService';
import { getClassSubjects, getPersonName } from '../../utils/classSubjectUtils';

function ClassDetailsPage() {
  const { classId } = useParams();
  const [classInfo, setClassInfo] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState('');

  const subjects = useMemo(() => getClassSubjects(classInfo), [classInfo]);

  useEffect(() => {
    let isMounted = true;

    async function loadClassDetails() {
      setIsLoading(true);
      setErrorMessage('');

      try {
        const classData = await getStudentClassById(classId);

        if (isMounted) {
          setClassInfo(classData);
        }
      } catch (error) {
        if (isMounted) {
          setErrorMessage(error.message || 'امکان دریافت اطلاعات کلاس وجود ندارد.');
        }
      } finally {
        if (isMounted) {
          setIsLoading(false);
        }
      }
    }

    loadClassDetails();

    return () => {
      isMounted = false;
    };
  }, [classId]);

  if (isLoading) {
    return (
      <div className="space-y-4">
        <BackNavLink to="/classes">بازگشت به کلاس‌ها</BackNavLink>
        <div className="rounded-[14px] bg-white p-6 text-center text-[12px] text-slate-500 shadow-[0_4px_14px_rgba(15,23,42,0.06)]">
          در حال دریافت اطلاعات کلاس...
        </div>
      </div>
    );
  }

  if (errorMessage && !classInfo) {
    return (
      <div className="space-y-4">
        <BackNavLink to="/classes">بازگشت به کلاس‌ها</BackNavLink>
        <div className="rounded-[14px] bg-white p-5 text-center shadow-[0_4px_14px_rgba(15,23,42,0.06)]">
          <p className="text-[13px] font-bold text-[#E02727]">{errorMessage}</p>
          <button
            type="button"
            onClick={() => window.location.reload()}
            className="mt-3 rounded-[6px] bg-[#00C0B9] px-5 py-1 text-[12px] font-bold text-white"
          >
            تلاش دوباره
          </button>
        </div>
      </div>
    );
  }

  if (!classInfo) {
    return (
      <div className="space-y-4">
        <BackNavLink to="/classes">بازگشت به کلاس‌ها</BackNavLink>
        <div className="rounded-[14px] bg-white p-5 text-center shadow-[0_4px_14px_rgba(15,23,42,0.06)]">
          <p className="text-[15px] font-bold text-slate-800">کلاس پیدا نشد</p>
          <p className="mt-2 text-[12px] text-slate-500">اطلاعات این کلاس در دسترس نیست.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-5">
      <BackNavLink to="/classes">بازگشت به کلاس‌ها</BackNavLink>

      <div className="flex items-center gap-2">
        <p
          style={{ background: 'linear-gradient(90deg, #75E3F7 0%, #00C7EA 100%)', boxShadow: '4px 5px 11px rgba(0, 0, 0, 0.07)' }}
          className="px-6 py-1 rounded-[10px] text-[15px] text-white"
        >
          {classInfo.title || 'کلاس'}
        </p>

        <img className="h-[50px]" src="/images/program.webp" alt="" />
      </div>

      <div
        className="overflow-hidden rounded-[14px] text-white"
        style={{ background: 'linear-gradient(20.43deg, rgba(84, 176, 248, 0.9) 0.9%, rgba(145, 73, 133, 0.9) 86.43%)', boxShadow: '4px 5px 11px rgba(0, 0, 0, 0.14)' }}
      >
        <div className="p-4">
          <p className="text-[17px] font-bold">{classInfo.title || 'کلاس بدون عنوان'}</p>
          <p className="mt-1 text-[11px] text-white/85">
            پایه {classInfo.grade?.title || 'نامشخص'} | ظرفیت {classInfo.studentsMaxCounts || 0} نفر
          </p>
        </div>
      </div>

      <div className="rounded-[14px] bg-white p-4 shadow-[0_4px_14px_rgba(15,23,42,0.06)]">
        <div className="flex items-center justify-between">
          <p className="text-[13px] font-bold text-slate-800">درس‌های کلاس</p>
          <span className="rounded-full bg-[#F8F8F8] px-3 py-1 text-[10px] text-[#914985]">
            {subjects.length} درس
          </span>
        </div>

        <div className="mt-3 flex flex-col gap-2">
          {subjects.length === 0 ? (
            <p className="rounded-[8px] bg-[#F8F8F8] px-3 py-4 text-center text-[11px] text-slate-500">
              درسی برای این کلاس ثبت نشده است.
            </p>
          ) : (
            subjects.map((classSubject) => {
              const teacher = classSubject.teacher;

              return (
                <div
                  key={classSubject.id}
                  className="flex items-center justify-between gap-3 rounded-[10px] bg-[#F8F8F8] p-2"
                >
                  <div className="flex min-w-0 items-center gap-2">
                    <img
                      className="h-[42px] w-[42px] shrink-0 rounded-full border border-white object-cover"
                      src={teacher?.avatar || '/images/profileimage.png'}
                      alt=""
                    />

                    <div className="min-w-0">
                      <p className="truncate text-[12px] font-bold text-slate-800">
                        {classSubject.subject?.title || 'درس بدون عنوان'}
                      </p>
                      <p className="text-[10px] text-slate-500">
                        {getPersonName(teacher, 'معلم ثبت نشده')}
                        {teacher?.phone ? ` | ${teacher.phone}` : ''}
                      </p>
                    </div>
                  </div>

                  <span className="rounded-[6px] bg-white px-2 py-1 text-[10px] text-[#00A8A2]">
                    معلم درس
                  </span>
                </div>
              );
            })
          )}
        </div>
      </div>

      <div className="rounded-[14px] bg-white p-4 shadow-[0_4px_14px_rgba(15,23,42,0.06)]">
        <p className="text-[13px] font-bold text-slate-800">خلاصه کلاس</p>
        <div className="mt-3 grid grid-cols-2 gap-2 text-[11px]">
          <div className="rounded-[8px] bg-[#F8F8F8] px-3 py-2 text-slate-700">
            پایه: {classInfo.grade?.title || 'نامشخص'}
          </div>
          <div className="rounded-[8px] bg-[#F8F8F8] px-3 py-2 text-slate-700">
            ظرفیت: {classInfo.studentsMaxCounts || 0} نفر
          </div>
        </div>
      </div>
    </div>
  );
}

export default ClassDetailsPage;
